import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import { ProgressProvider, useProgress, STEP_ORDER, type StepKey } from '../state/ProgressContext';
import { ProgressHeader } from '../components/ProgressHeader';
import { TopBar } from '../components/TopBar';
import { activePosition, positionForRole } from '../content/positions';
import { POSITION_DISCONTINUED_MESSAGE } from '../lib/applicationAvailability';

const positionSteps = activePosition.workflow.flatMap((group) => group.steps);
// Several steps share one route - Application Questions and Application Review are the same
// page, since the review status now shows inline on the page the applicant submitted from.
// A shared route unlocks with the EARLIEST step that uses it, so the first mapping wins.
// Taking the last (what Object.fromEntries does) resolved /onboarding/application to
// APPLICATION_REVIEW, which is locked until the application is submitted - so StepGuard sent
// every applicant still filling the form back to the page they were already on, and the
// questions never rendered at all.
const ROUTE_TO_STEP = positionSteps.reduce<Record<string, StepKey>>((routes, step) => {
  if (!(step.route in routes)) routes[step.route] = step.key;
  return routes;
}, {});
const STEP_ROUTES = Object.fromEntries(positionSteps.map((step) => [step.key, step.route])) as Record<StepKey, string>;

export function OnboardingLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="center-screen"><div className="loading-state">Loading…</div></div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.isAdmin) return <Navigate to="/admin" replace />;

  return (
    <ProgressProvider>
      <OnboardingChrome />
    </ProgressProvider>
  );
}

function OnboardingChrome() {
  const { progress } = useProgress();
  const position = positionForRole(progress?.roleId);
  // The position this application belongs to has been discontinued, so there is
  // no step left to show: every step route refuses it server-side anyway, and
  // the progress bar would only invite an applicant to keep clicking.
  if (progress?.positionDiscontinued) {
    return <div className="app-shell">
      <TopBar applicationName={position.listing.title} />
      <div className="page-container"><PositionDiscontinued title={position.listing.title} /></div>
    </div>;
  }
  return <div className="app-shell">
    <TopBar applicationName={position.listing.title} />
    <ProgressHeader />
    <div className="page-container"><StepGuard><Outlet /></StepGuard></div>
  </div>;
}

function PositionDiscontinued({ title }: { title: string }) {
  return <div>
    <h1 className="step-title">{title}</h1>
    <p className="step-subtitle">This position is no longer open.</p>
    <div className="card position-closed-card">
      <p>{POSITION_DISCONTINUED_MESSAGE}</p>
      <p>Thank you for the time you put into this application. Any other position that is still open is on the careers page.</p>
      <Link className="btn-primary" to="/">View open positions</Link>
    </div>
  </div>;
}

function StepGuard({ children }: { children: React.ReactNode }) {
  const { progress, loading, fresh } = useProgress();
  const location = useLocation();

  if (loading || !progress) return <div className="loading-state">Loading your progress…</div>;
  const holdUntilFresh = <div className="loading-state">Loading your progress…</div>;

  // A field applicant's location is only settled once they have picked a market Krovance
  // covers: the position is not offered anywhere else, and every step route refuses them
  // without one, so the Location step is where they belong until they have one.
  const marketOk = !progress.market?.required || Boolean(progress.market.id);
  const countryOk = progress.country?.confirmed && progress.country.eligibility === 'ELIGIBLE' && Boolean(progress.country.code) && marketOk;
  if (!countryOk && location.pathname !== '/onboarding/country') {
    if (!fresh) return holdUntilFresh;
    return <Navigate to="/onboarding/country" replace />;
  }
  const requestedStep = ROUTE_TO_STEP[location.pathname];
  if (requestedStep) {
    const requestedIndex = STEP_ORDER.indexOf(requestedStep);
    const unlockedRoute = STEP_ROUTES[progress.currentStep] ?? '/onboarding/training';
    // The backend is the real authority on step access; this redirect is
    // purely so a user can't sit on a locked step's URL in the UI. Every
    // API call the step page makes is independently guarded server-side.
    // Redirecting to the page we are already on would render nothing forever
    // instead of redirecting, so in that case show the page.
    if (requestedIndex > progress.highestUnlockedStepIndex && unlockedRoute !== location.pathname) {
      if (!fresh) return holdUntilFresh;
      return <Navigate to={unlockedRoute} replace />;
    }
  }

  return <>{children}</>;
}
